import { useMemo } from "react";

import { useCachedDisplayData } from "../firebase";
import { getUrlSearchParams } from "../otherHelpers";
import { isSharedProduct } from "./explorerProductsTab";

const MAX_ENTRIES = 5;

const isPendingShare = (entry) =>
  entry?.status === "pending" || entry?.status === "unconfirmed";

const isAcceptedShare = (entry) => entry?.status === "accepted";

const isRevokedShare = (entry) => entry?.status === "revoked";

const hasReceivedProduct = (relationship) =>
  (relationship.sharedProducts || []).some(isSharedProduct);

const hasConfirmedReport = (relationship) =>
  (relationship.sharedReports || []).some(({ isFinalized }) => isFinalized);

const hasConfirmedProduct = (relationship) =>
  (relationship.sharedProducts || []).some(
    ({ bomShared, reportShared, isFinalized }) =>
      (bomShared || reportShared) && isFinalized
  );

const findLatestSharedYear = (relationship) => {
  const reportYears = (relationship.sharedReports || []).map(({ year }) =>
    Number(year)
  );
  const surveyYears = (relationship.surveyData || [])
    .filter(({ status }) => status === "accepted")
    .map(({ year }) => Number(year));
  const all = [...reportYears, ...surveyYears].filter(Number.isFinite);
  if (!all.length) {
    return null;
  }
  return Math.max(...all);
};

export const buildPendingSupplierShares = (relationships) =>
  (relationships || [])
    .flatMap((relationship) =>
      (relationship.surveyData || [])
        .filter(isPendingShare)
        .map(({ year }) => ({
          relationshipId: relationship.id,
          vendorName: relationship.vendorName || "Unnamed supplier",
          year: Number(year)
        }))
    )
    .slice(0, MAX_ENTRIES);

const buildConfirmedItems = (relationships) =>
  (relationships || [])
    .filter(
      (rel) =>
        hasConfirmedReport(rel) ||
        hasConfirmedProduct(rel) ||
        (rel.surveyData || []).some(isAcceptedShare)
    )
    .map((rel) => ({
      relationshipId: rel.id,
      vendorName: rel.vendorName || "Unnamed supplier",
      latestYear: findLatestSharedYear(rel),
      hasReport: hasConfirmedReport(rel),
      hasBom: hasConfirmedProduct(rel)
    }))
    .slice(0, MAX_ENTRIES);

const buildSummary = (relationships) => {
  const totalRelationshipCount = (relationships || []).length;
  const pending = buildPendingSupplierShares(relationships);
  const confirmedItems = buildConfirmedItems(relationships);
  const confirmedSharesCount = (relationships || []).filter(
    (rel) =>
      hasConfirmedReport(rel) ||
      hasConfirmedProduct(rel) ||
      (rel.surveyData || []).some(isAcceptedShare)
  ).length;
  const revokedSharesCount = (relationships || []).filter((rel) =>
    (rel.surveyData || []).some(isRevokedShare)
  ).length;
  const receivedProductsCount = (relationships || []).filter(
    hasReceivedProduct
  ).length;
  return {
    totalRelationshipCount,
    pendingSharesCount: pending.length,
    confirmedSharesCount,
    revokedSharesCount,
    receivedProductsCount,
    confirmedItems
  };
};

export const useSuppliersTabContext = () => {
  const [vendorRelationships] = useCachedDisplayData("vendor-relationships");

  return useMemo(() => {
    const focusedSupplierRelationshipId = getUrlSearchParams("selectedId");
    return {
      suppliersSummary: buildSummary(vendorRelationships),
      pendingSupplierShares: buildPendingSupplierShares(vendorRelationships),
      focusedSupplierRelationshipId: focusedSupplierRelationshipId || null
    };
  }, [vendorRelationships]);
};
